import { useState } from "react";
import axios from "axios";
import { Loader2, Send, HeartHandshake, Building2, PackagePlus } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../lib/anim";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field = "w-full rounded-xl border hairline px-4 py-3 outline-none focus:ring-2 focus:ring-brand-sky/40 bg-white";

export const GetInvolved = () => {
  const [tab, setTab] = useState("kit");
  const [kit, setKit] = useState({ name: "", email: "", phone: "", donation_type: "Assembled hygiene kits", quantity: "", dropoff: "", message: "" });
  const [vol, setVol] = useState({ name: "", email: "", phone: "", city: "", interest: "Hygiene Kit Assembly", availability: "", message: "" });
  const [con, setCon] = useState({ name: "", email: "", organization: "", subject: "Partnership Inquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const submitKit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/kit-donations`, kit);
      toast.success(r.data.message);
      setKit({ name: "", email: "", phone: "", donation_type: "Assembled hygiene kits", quantity: "", dropoff: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const submitVol = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/volunteer`, vol);
      toast.success(r.data.message);
      setVol({ name: "", email: "", phone: "", city: "", interest: "Hygiene Kit Assembly", availability: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const submitCon = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/contact`, con);
      toast.success(r.data.message);
      setCon({ name: "", email: "", organization: "", subject: "Partnership Inquiry", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const TabBtn = ({ id, icon: Icon, children, testid }) => (
    <button
      data-testid={testid}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${tab === id ? "bg-white text-brand-ink" : "bg-white/10 text-white/70 hover:bg-white/15"}`}
    >
      <Icon className="h-4 w-4" /> {children}
    </button>
  );

  return (
    <section id="involved" data-testid="get-involved" className="py-24 md:py-32 bg-brand-ink text-white relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-brand-sky font-bold mb-4">Get Involved</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl leading-[1.02]">
            Donate a kit. Volunteer. Partner your clinic.
          </h2>
          <p className="mt-5 text-white/60 max-w-2xl text-lg">
            Help us reach our goal of distributing <span className="text-brand-green font-semibold">200 hygiene kits this summer</span>.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3">
          <TabBtn id="kit" icon={PackagePlus} testid="tab-kit">Donate a Kit</TabBtn>
          <TabBtn id="volunteer" icon={HeartHandshake} testid="tab-volunteer">Volunteer</TabBtn>
          <TabBtn id="partner" icon={Building2} testid="tab-partner">Partner / Contact</TabBtn>
        </div>

        <div className="mt-8 bg-white text-brand-ink rounded-[2rem] p-8 md:p-10 shadow-2xl">
          {tab === "kit" && (
            <form data-testid="kit-form" onSubmit={submitKit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="Full name" data-testid="kit-name" className={field} value={kit.name} onChange={(e) => setKit({ ...kit, name: e.target.value })} />
              <input required type="email" placeholder="Email" data-testid="kit-email" className={field} value={kit.email} onChange={(e) => setKit({ ...kit, email: e.target.value })} />
              <input placeholder="Phone (optional)" data-testid="kit-phone" className={field} value={kit.phone} onChange={(e) => setKit({ ...kit, phone: e.target.value })} />
              <select data-testid="kit-type" className={field} value={kit.donation_type} onChange={(e) => setKit({ ...kit, donation_type: e.target.value })}>
                <option>Assembled hygiene kits</option>
                <option>Individual hygiene items</option>
                <option>Bulk supplies</option>
                <option>Feminine hygiene products</option>
              </select>
              <input placeholder="How many kits / items?" data-testid="kit-quantity" className={field} value={kit.quantity} onChange={(e) => setKit({ ...kit, quantity: e.target.value })} />
              <select data-testid="kit-dropoff" className={field} value={kit.dropoff} onChange={(e) => setKit({ ...kit, dropoff: e.target.value })}>
                <option value="">Preferred drop-off / pickup</option>
                <option>I'll drop off at Lifeline Primary Care</option>
                <option>Please arrange a pickup</option>
                <option>Ship to AMCares</option>
              </select>
              <textarea placeholder="Anything we should know? (optional)" data-testid="kit-message" rows={3} className={`${field} md:col-span-2`} value={kit.message} onChange={(e) => setKit({ ...kit, message: e.target.value })} />
              <button data-testid="kit-submit" disabled={loading} className="md:col-span-2 bg-brand-green text-white hover:bg-brand-greendark disabled:opacity-60 transition-colors rounded-full py-4 font-semibold flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <PackagePlus className="h-4 w-4" />} Pledge my hygiene kit donation
              </button>
              <p className="md:col-span-2 text-center text-xs text-brand-ink/40">We accept in-kind hygiene kits and supplies — no monetary donations needed.</p>
            </form>
          )}

          {tab === "volunteer" && (
            <form data-testid="volunteer-form" onSubmit={submitVol} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="Full name" data-testid="vol-name" className={field} value={vol.name} onChange={(e) => setVol({ ...vol, name: e.target.value })} />
              <input required type="email" placeholder="Email" data-testid="vol-email" className={field} value={vol.email} onChange={(e) => setVol({ ...vol, email: e.target.value })} />
              <input placeholder="Phone (optional)" data-testid="vol-phone" className={field} value={vol.phone} onChange={(e) => setVol({ ...vol, phone: e.target.value })} />
              <input placeholder="City" data-testid="vol-city" className={field} value={vol.city} onChange={(e) => setVol({ ...vol, city: e.target.value })} />
              <select data-testid="vol-interest" className={field} value={vol.interest} onChange={(e) => setVol({ ...vol, interest: e.target.value })}>
                <option>Hygiene Kit Assembly</option>
                <option>Community Distribution</option>
                <option>Health Education</option>
                <option>Fundraising & Events</option>
                <option>Youth Leadership</option>
              </select>
              <input placeholder="Availability (e.g., weekends)" data-testid="vol-availability" className={field} value={vol.availability} onChange={(e) => setVol({ ...vol, availability: e.target.value })} />
              <textarea placeholder="Tell us a little about yourself (optional)" data-testid="vol-message" rows={3} className={`${field} md:col-span-2`} value={vol.message} onChange={(e) => setVol({ ...vol, message: e.target.value })} />
              <button data-testid="vol-submit" disabled={loading} className="md:col-span-2 bg-brand-green text-white hover:bg-brand-greendark disabled:opacity-60 transition-colors rounded-full py-4 font-semibold flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />} Sign up to volunteer
              </button>
            </form>
          )}

          {tab === "partner" && (
            <form data-testid="contact-form" onSubmit={submitCon} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder="Full name" data-testid="con-name" className={field} value={con.name} onChange={(e) => setCon({ ...con, name: e.target.value })} />
              <input required type="email" placeholder="Email" data-testid="con-email" className={field} value={con.email} onChange={(e) => setCon({ ...con, email: e.target.value })} />
              <input placeholder="Organization / Clinic (optional)" data-testid="con-org" className={field} value={con.organization} onChange={(e) => setCon({ ...con, organization: e.target.value })} />
              <select data-testid="con-subject" className={field} value={con.subject} onChange={(e) => setCon({ ...con, subject: e.target.value })}>
                <option>Partnership Inquiry</option>
                <option>Clinic Collaboration</option>
                <option>Hygiene Kit Donation</option>
                <option>Media & Press</option>
                <option>General Question</option>
              </select>
              <textarea required placeholder="How can we work together?" data-testid="con-message" rows={4} className={`${field} md:col-span-2`} value={con.message} onChange={(e) => setCon({ ...con, message: e.target.value })} />
              <button data-testid="con-submit" disabled={loading} className="md:col-span-2 bg-brand-blue text-white hover:bg-brand-bluedark disabled:opacity-60 transition-colors rounded-full py-4 font-semibold flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />} Send message
              </button>
            </form>
          )}
        </div>
      </div>
      <div className="absolute -top-32 -left-20 w-[32rem] h-[32rem] rounded-full bg-brand-blue/20 blur-3xl" />
    </section>
  );
};
